/**
 * Shared utility for building Example[] from admin TestConfiguration.
 *
 * Used by the admin statement preview to render examples identically to
 * the student-facing DescriptionPane, without needing to run generate-mdx.
 */

import type {
  OperationItem,
  TestConfiguration,
} from '../components/admin/problems/problem-editor/tests-section.js'
import type {
  ClassExample,
  Example,
  FunctionExample,
} from '../components/editor/problem-context.js'
import { TEST_TYPE } from './constants/test-types.js'

export interface ExampleError {
  index: number
  message: string
}

export interface BuildExamplesResult {
  examples: Example[]
  errors: ExampleError[]
}

/**
 * Build Example[] from admin TestConfiguration for preview rendering.
 * Each test case transformation is individually wrapped in try/catch
 * so a single bad test case doesn't prevent the rest from rendering.
 */
export function buildExamplesFromTestConfig(
  testConfig: TestConfiguration,
): BuildExamplesResult {
  if (testConfig.testType === TEST_TYPE.CLASS) {
    return buildClassExamples(testConfig)
  }
  return buildFunctionExamples(testConfig)
}

function buildFunctionExamples(
  testConfig: TestConfiguration,
): BuildExamplesResult {
  const examples: Example[] = []
  const errors: ExampleError[] = []

  const exampleCases = (testConfig.testCases || []).filter(
    (tc) => tc.showAsExample,
  )
  // Test-only params are invisible to the student, so the preview hides them
  // too — otherwise the admin statement shows a param the problem never has.
  const sortedParams = [...(testConfig.inputParameters || [])]
    .filter((p) => p.userFacing !== false)
    .sort((a, b) => a.order - b.order)

  exampleCases.forEach((tc, i) => {
    const exampleIndex = i + 1
    try {
      const formattedInputs: Record<string, string> = {}
      const inputsAfter: Record<string, string> = {}
      for (const param of sortedParams) {
        const value = tc.inputs?.[param.name]
        if (value !== undefined) {
          formattedInputs[param.name] = value
        }
        // Already a string here: the admin form edits raw JSON text. A
        // cleared textarea leaves `''` in form state, which the save path
        // drops — the preview has to drop it too, or an admin mid-edit sees
        // the blank Output this ticket exists to remove.
        const afterValue = tc.expectedInputsAfter?.[param.name]
        if (afterValue !== undefined && afterValue.trim() !== '') {
          inputsAfter[param.name] = afterValue
        }
      }

      examples.push({
        index: exampleIndex,
        inputs: formattedInputs,
        output: tc.expectedOutput ?? '',
        expectedInputsAfter:
          Object.keys(inputsAfter).length > 0 ? inputsAfter : undefined,
        inputDisplayOverride: tc.inputDisplayOverride || undefined,
        outputDisplayOverride: tc.outputDisplayOverride || undefined,
        imageFilename: tc.imageFilename || undefined,
      } satisfies FunctionExample)
    } catch (err) {
      errors.push({
        index: exampleIndex,
        message: err instanceof Error ? err.message : 'Failed to build example',
      })
    }
  })

  return { examples, errors }
}

function buildClassExamples(
  testConfig: TestConfiguration,
): BuildExamplesResult {
  const examples: Example[] = []
  const errors: ExampleError[] = []

  const exampleCases = (testConfig.classTestCases || []).filter(
    (tc) => tc.showAsExample,
  )

  exampleCases.forEach((tc, i) => {
    const exampleIndex = i + 1
    try {
      // Filter comments from operations, keep only actual operations
      const operations = (tc.operations || [])
        .filter((op): op is OperationItem => op.type !== 'comment')
        .map((op) => ({
          method: op.method,
          args: op.args,
          expected: op.expected,
          ...(op.expectsError && { expectsError: true }),
        }))

      examples.push({
        index: exampleIndex,
        constructorArgs: tc.constructorArgs || undefined,
        operations,
        intent: tc.intent || undefined,
        inputDisplayOverride: tc.inputDisplayOverride || undefined,
        outputDisplayOverride: tc.outputDisplayOverride || undefined,
      } satisfies ClassExample)
    } catch (err) {
      errors.push({
        index: exampleIndex,
        message: err instanceof Error ? err.message : 'Failed to build example',
      })
    }
  })

  return { examples, errors }
}
